import { createContext, useContext } from 'react'
import type { AuthUser, LoginCredentials, LoginOutcome } from '@/types/domain/auth'

/*
 * The context object and its hook live apart from <AuthProvider> on purpose.
 * React Fast Refresh can only hot-swap a file that exports components alone; a
 * file that also exports a hook is re-executed on every edit, and re-executing
 * `createContext` mints a SECOND context. The mounted provider keeps supplying
 * the old one, so any lazily loaded screen imported afterwards reads the new,
 * empty one and throws 'useAuth must be used inside <AuthProvider>'. Kept here,
 * the context is created once and survives edits to the provider.
 */

/**
 * The auth session lives here — user, role and permissions — NOT in Zustand and
 * NOT in React Query as app state. Everything else fetched from the API belongs
 * to React Query.
 */
export interface AuthContextValue {
  readonly user: AuthUser | null
  readonly isAuthenticated: boolean
  /** True while the persisted refresh token is being exchanged for a session. */
  readonly isRestoring: boolean
  /**
   * True when a stored session could not be restored because the refresh call
   * got no verdict — rate limited, a 5xx, a timeout, or offline — as opposed to
   * being refused. The refresh token is still stored and probably still valid,
   * so the user is offered a retry rather than the login screen.
   */
  readonly restoreFailed: boolean
  /**
   * Granted permission keys, in the API's own vocabulary. Read it through
   * `usePermission()` rather than directly.
   */
  readonly permissions: ReadonlySet<string>
  /**
   * Step one of sign-in. Resolves `signedIn` (session established) or
   * `mfaRequired` (a code was emailed; finish with `verifyLoginOtp`).
   */
  readonly login: (credentials: LoginCredentials) => Promise<LoginOutcome>
  /** Step two: the emailed code. Establishes the session on success. */
  readonly verifyLoginOtp: (challengeId: string, otp: string) => Promise<void>
  readonly logout: () => Promise<void>
  readonly refresh: () => Promise<void>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>.')
  return context
}
